/**
 * Inline text is for replies, chips are tools (Chris, 2026-10-27).
 *
 * A reply is what the person might say next: a continue_conversation path,
 * a gift opener's `inline` branch, or a room question the page marks `reply`
 * ("What do Sam's stars say about us?"). Each is a link in the Mystic's line:
 * where its words stand in the sentence (case-insensitive, first appearance;
 * the server weaves starters in as quoted words), else after what she said,
 * in the same bubble. A tap is said as the person's words, so it echoes as
 * their bubble, and it never leaves the session.
 *
 * Everything else is a tool (Select Cards, Share, Finish, Talk it through...)
 * and is handed back as `leftover`, to be a chip. A reply is never a chip: with
 * no line to sit in yet, it waits for one. "Something else" is dropped: the
 * text box covers it.
 *
 * @param {string} text        the Mystic's message
 * @param {Array}  actions     the in-chat buttons
 * @returns {{segments: Array<{text: string, action?: object, joiner?: boolean}>, leftover: Array}}
 */
// Lower case with curly quotes made straight. Same length as the text, so a
// position found here is a position in the Mystic's own words.
const fold = (s) => s.toLowerCase().replace(/[’‘]/g, "'").replace(/[“”]/g, '"')
// A label's end punctuation ("…hoping for?") is not part of the phrase.
const phrase = (s) => s.trim().replace(/[\s.?!,;:]+$/, '')
const OPEN_Q = new Set(['“', '"'])
const CLOSE_Q = new Set(['”', '"'])

/** Something the person would say, as opposed to a tool. */
export function isReply (action) {
  if (!action) return false
  return !!action.reply || !!action.inline || action.action_type === 'continue_conversation'
}
const labelled = (a) => !!(a && (a.button_label || '').trim())

// The way out ("Something else", "Something else on my mind"): the text box.
const wayOut = (a) => !!a.focus_composer || /^something else\b/i.test((a.button_label || '').trim())

export function inlineChoices (text, actions) {
  const all = Array.isArray(actions) ? actions.filter(Boolean) : []
  const source = typeof text === 'string' ? text : ''
  const replies = all.filter((a) => isReply(a) && labelled(a) && !wayOut(a))
  const leftover = all.filter((a) => !isReply(a))
  if (!source) return { segments: [], leftover }
  const hay = fold(source)
  const hits = []
  const after = []
  for (const a of replies) {
    const want = fold(phrase(a.button_label))
    const at = want ? hay.indexOf(want) : -1
    const overlaps = at !== -1 && hits.some(h => at < h.end && at + want.length > h.start)
    if (at === -1 || overlaps) { after.push(a); continue }
    // Quoted where the Mystic wove it in (“Where I stand now”): the quotes go
    // with the link, so the line never breaks between a quote and its words.
    let start = at
    let end = at + want.length
    const punct = /[?!.]/.test(source[end] || '') ? 1 : 0
    if (start > 0 && OPEN_Q.has(source[start - 1]) && CLOSE_Q.has(source[end + punct] || '')) { start -= 1; end += 1 + punct }
    hits.push({ start, end, action: a })
  }
  hits.sort((x, y) => x.start - y.start)
  const segments = []
  let cursor = 0
  for (const h of hits) {
    if (h.start > cursor) segments.push({ text: source.slice(cursor, h.start) })
    segments.push({ text: source.slice(h.start, h.end), action: h.action })
    cursor = h.end
  }
  if (cursor < source.length) segments.push({ text: source.slice(cursor) })
  after.forEach((a, i) => {
    segments.push({ text: i ? ' · ' : '\n', joiner: true })
    segments.push({ text: a.button_label.trim(), action: a })
  })
  return { segments, leftover }
}
